import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';

import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

enum NavigatorType {
  PushScreen = 'PUSH_SCREEN',
  ReplaceScreen = 'REPLACE_SCREEN',
  PopToTop = 'POP_TO_TOP',
  Back = 'BACK',
  JumpTo = 'JUMP_TO',
  Navigate = 'NAVIGATE',
  // Reset = 'RESET' 추가 예정
}

interface QueryParams {
  params?: Record<string, any>;
}

interface ScreenParams extends QueryParams {
  url: string;
}

interface AppScreenParams extends QueryParams {
  screenName: string;
}

interface JumpToScreenParams extends QueryParams {
  tabName: string;
  url: string;
}

interface JumpToAppScreenParams extends QueryParams {
  tabName: string;
  screenName: string;
}
interface Options {
  disabledWebRoute?: boolean;
}

const useNavigator = <T = ParsedUrlQuery>() => {
  const { push, replace, ...routerProps } = useRouter();
  const query = routerProps.query as T;

  const pushScreen = ({ params, url }: ScreenParams, options?: Options) => {
    if (isWebView()) {
      return window.history.replaceState(
        {},
        '',
        `${addQueryString({
          ...query,
          routeType: NavigatorType.PushScreen,
          nextUrl: `${url}${addQueryString({ ...params })}`,
        })}`
      );
    }
    if (options?.disabledWebRoute) {
      return;
    }

    return push(`${url}${addQueryString(params)}`);
  };

  const popToTopScreen = (url?: string, options?: Options) => {
    if (isWebView()) {
      return window.history.replaceState({}, '', `?routeType=${NavigatorType.PopToTop}`);
    }
    if (!url) {
      return;
    }
    if (options?.disabledWebRoute) {
      return;
    }
    return push(url);
  };

  const jumpToScreen = ({ tabName, params, url }: JumpToScreenParams, options?: Options) => {
    if (isWebView()) {
      return window.history.replaceState(
        {},
        '',
        `${addQueryString({
          ...query,
          tabName,
          routeType: NavigatorType.JumpTo,
          nextUrl: `${url}${addQueryString({ ...params })}`,
        })}`
      );
    }
    if (options?.disabledWebRoute) {
      return;
    }
    return push(`${url}${addQueryString(params)}`);
  };

  const replaceScreen = ({ params, url }: ScreenParams, options?: Options) => {
    if (isWebView()) {
      return window.history.replaceState(
        {},
        '',
        `${addQueryString({
          ...query,
          routeType: NavigatorType.ReplaceScreen,
          nextUrl: `${url}${addQueryString({ ...params })}`,
        })}`
      );
    }
    if (options?.disabledWebRoute) {
      return;
    }
    return replace(`${url}${addQueryString(params)}`);
  };

  const navigateScreen = ({ params, url }: ScreenParams, options?: Options) => {
    if (isWebView()) {
      return window.history.replaceState(
        {},
        '',
        `${addQueryString({
          ...query,
          routeType: NavigatorType.Navigate,
          nextUrl: `${url}${addQueryString({ ...params })}`,
        })}`
      );
    }
    if (options?.disabledWebRoute) {
      return;
    }
    return replace(`${url}${addQueryString(params)}`);
  };

  const back = () => {
    if (isWebView()) {
      return window.history.replaceState({}, '', addQueryString({ routeType: NavigatorType.Back }));
    }
    return window.history.back();
  };

  const pushAppScreen = ({ screenName, params }: AppScreenParams) => {
    return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
      screenName,
      ...(params && { params }),
    });
  };

  const jumpToAppScreen = ({ tabName, screenName, params }: JumpToAppScreenParams) => {
    return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_TAB_HOME, {
      tabName,
      screenName,
      ...(params && { params }),
    });
  };

  const replaceAppScreen = ({ screenName, params }: AppScreenParams) => {
    return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE, {
      screenName,
      ...(params && { params }),
    });
  };

  const navigateAppScreen = ({ screenName, params }: AppScreenParams) => {
    return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.NAVIGATE_TO_PAGE, {
      screenName,
      ...(params && { params }),
    });
  };

  const openExternalBrowser = ({ url }: { url: string }) => {
    if (isWebView()) {
      return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER, {
        url,
      });
    }
    return window.open(url);
  };

  return {
    ...routerProps,
    query,
    push,
    pushScreen,
    popToTopScreen,
    jumpToScreen,
    replace,
    replaceScreen,
    navigateScreen,
    pushAppScreen,
    jumpToAppScreen,
    replaceAppScreen,
    navigateAppScreen,
    back,
    openExternalBrowser,
  };
};

export default useNavigator;
